/**
 * Contrato da conta bancária (spec 02 §9.1 · passo 4.4b).
 *
 * **O saldo NÃO está aqui, e a ausência é deliberada.** A posição de caixa nasce no 4.6
 * (`v_saldo_conta`), e devolvê-la nesta rota criaria a segunda fonte do número que a
 * tela 6.2 vai mostrar — o defeito que este repositório passa o tempo todo evitando.
 * Esta rota existe para UMA coisa: alimentar o seletor do modal de baixa do 4.5, para
 * que ele não nasça com as contas fixas no código (a lição que a DS-100 e a DS-127 já
 * pagaram duas vezes).
 */
import { z } from 'zod'
import { codigoDeMoeda, dataNominal, uuid } from './comum.js'

export const tipoDeContaBancaria = z.enum(['corrente', 'aplicacao', 'caixa'])
export type TipoDeContaBancaria = z.infer<typeof tipoDeContaBancaria>

export const integracaoDeConta = z.enum(['nenhuma', 'sicoob_api', 'arquivo'])
export type IntegracaoDeConta = z.infer<typeof integracaoDeConta>

/** O rótulo que a UI mostra no seletor. Copiado do GLOSSARIO, não inventado aqui. */
export const ROTULO_DO_TIPO_DE_CONTA: Readonly<Record<TipoDeContaBancaria, string>> = {
  corrente: 'Conta corrente',
  aplicacao: 'Aplicação',
  caixa: 'Caixa',
}

/**
 * Cadastro de conta bancária — o modal "Nova conta bancária" da 6.2.
 *
 * ## A moeda é ÚNICA e imóvel, e por isso ela entra aqui
 *
 * O banco tem gatilho que recusa trocá-la depois (0024): saldo inicial, liquidações e
 * movimentos importados são todos valores NAQUELA moeda, e trocá-la reinterpretaria o
 * histórico inteiro sem converter nada — o pior tipo de erro, porque os números
 * continuariam os mesmos com outro significado.
 *
 * ## O saldo inicial NÃO se inventa
 *
 * Ele é a abertura na data informada, e o padrão é zero. Para conta com integração, o
 * saldo verdadeiro vem do banco; digitar um palpite aqui criaria a divergência que a
 * conciliação depois passaria semanas perseguindo.
 */
export const entradaDeContaBancaria = z.object({
  nome: z.string().trim().min(1, 'Informe um nome para a conta.'),
  /** A PJ dona da conta; é dado do registro e não o filtro atualmente aberto. */
  entityId: uuid,
  tipo: tipoDeContaBancaria,
  banco: z.string().nullable().optional(),
  agencia: z.string().nullable().optional(),
  numero: z.string().nullable().optional(),
  moeda: codigoDeMoeda,
  integracao: integracaoDeConta,
  saldoInicial: z.string(),
  saldoInicialEm: dataNominal,
})
export type EntradaDeContaBancaria = z.infer<typeof entradaDeContaBancaria>

/** Edição do cadastro; a presença de `moeda` permite devolver o 422 explicável. */
export const entradaDeEdicaoDeContaBancaria = z.object({
  nome: z.string().trim().min(1, 'Informe um nome para a conta.'),
  tipo: tipoDeContaBancaria,
  banco: z.string().nullable(),
  agencia: z.string().nullable(),
  numero: z.string().nullable(),
  moeda: codigoDeMoeda,
  integracao: integracaoDeConta,
  ativa: z.boolean(),
}).partial().refine((e) => Object.keys(e).length > 0, 'Informe ao menos um campo para editar.')
export type EntradaDeEdicaoDeContaBancaria = z.infer<typeof entradaDeEdicaoDeContaBancaria>

export const contaBancaria = z.object({
  id: uuid,
  nome: z.string(),
  tipo: tipoDeContaBancaria,
  banco: z.string().nullable(),
  agencia: z.string().nullable(),
  numero: z.string().nullable(),
  moeda: codigoDeMoeda,
  integracao: integracaoDeConta,
})
export type ContaBancaria = z.infer<typeof contaBancaria>
